// DashboardLayout.jsx — Monochrome Desert Glassmorphism Layout
import React, { useState } from 'react';
import { UserCircle, LogOut, Menu, X, Moon, Sun, Facebook, Youtube, MessageCircle, Home, Instagram } from 'lucide-react';

// ──────────────────────────────────────────────
// Main Export
// ──────────────────────────────────────────────
export const DashboardLayout = ({
  menuItems,          // [{ id, label, Icon (lucide component), count?, badge? }]
  activeTab,
  setActiveTab,
  isDarkMode,
  setIsDarkMode,
  signOut,
  profile,            // { fullName?, firstName?, lastName?, grade?, role? }
  sidebarOpen,
  setSidebarOpen,
  bottomSlot,         // optional extra JSX rendered at bottom of sidebar
  children,
}) => {
  const displayName =
    profile?.fullName ||
    [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ') ||
    'مستخدم';

  const [showHelpMenu, setShowHelpMenu] = useState(false);

  // ── Monochrome tokens ────────────────────────
  const d = isDarkMode;

  return (
    <div
      className="min-h-screen relative"
      dir="rtl"
      style={{
        backgroundImage: `url('/images/${d ? 'desert-dark' : 'desert-light'}.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: d ? '#ffffff' : '#000000',
      }}
    >
      {/* Full-page overlay for readability */}
      <div
        className="fixed inset-0 pointer-events-none z-0 transition-colors duration-500"
        style={{
          background: d
            ? 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.75) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.7) 100%)'
        }}
      />

      {/* ══════════════ TOP NAVBAR ══════════════ */}
      <header
        className="fixed top-0 right-0 left-0 h-16 z-50 flex items-center justify-between px-6 transition-colors duration-300"
        style={{
          background: d ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {/* Platform name – RIGHT side (RTL) */}
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-lg ${d ? 'bg-white' : 'bg-black'}`}>
            <span className={`font-black text-base ${d ? 'text-black' : 'text-white'}`}>♞</span>
          </div>
          <h1 className="text-lg font-black tracking-tight">
            <span className={d ? 'text-white' : 'text-black'}>سبيل</span>
            <span className={d ? ' text-gray-400' : ' text-gray-600'}> التفوق</span>
          </h1>
        </div>

        {/* LEFT side: toggle + avatar + logout */}
        <div className="flex items-center gap-3">
          {/* Day/Night toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 rounded-full transition-all duration-300 ${d ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-black'}`}
            aria-label="Toggle theme"
          >
            {d ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Avatar pill */}
          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl ${d ? 'bg-white/10 border border-white/15' : 'bg-black/10 border border-black/10'}`}>
            <UserCircle size={20} className={d ? 'text-gray-300' : 'text-gray-600'} />
            <span className={`text-sm font-bold ${d ? 'text-gray-200' : 'text-gray-700'}`}>{displayName}</span>
          </div>

          <button
            onClick={signOut}
            className={`p-2 rounded-xl transition ${d ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-black hover:bg-black/10'}`}
            title="تسجيل الخروج"
          >
            <LogOut size={18} />
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`lg:hidden p-2 rounded-xl transition ${d ? 'hover:bg-white/10' : 'hover:bg-black/10'}`}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* ══════════════ SIDEBAR ══════════════ */}
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-16 right-0 h-[calc(100vh-4rem)] w-64 flex flex-col z-40 transition-all duration-300 overflow-y-auto
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}
        style={{
          background: d ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderLeft: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {/* User card */}
        <div className={`p-6 text-center ${d ? 'border-b border-white/10' : 'border-b border-black/10'}`}>
          <div className="flex flex-col items-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-3 shadow-lg ${d ? 'bg-white/10 border-2 border-white/20' : 'bg-black/10 border-2 border-black/15'}`}>
              <UserCircle size={52} className={d ? 'text-gray-400' : 'text-gray-500'} />
            </div>
            <h3 className="font-bold text-base leading-tight">{displayName}</h3>
            {profile?.grade && (
              <p className={`text-xs mt-1 ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                {profile.grade === '1sec' ? 'الصف الأول الثانوي' :
                 profile.grade === '2sec' ? 'الصف الثاني الثانوي' :
                 profile.grade === '3sec' ? 'الصف الثالث الثانوي' : profile.grade}
              </p>
            )}
            {(profile?.role === 'admin' || profile?.role === 'boss') && (
              <span className={`mt-2 inline-block text-xs font-bold px-3 py-1 rounded-full ${d ? 'bg-white/10 text-gray-300' : 'bg-black/10 text-gray-700'}`}>
                {profile.role === 'boss' ? '👑 المدير الأعلى' : '🛡️ مدير'}
              </span>
            )}
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.Icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 font-bold text-sm ${
                  isActive
                    ? d
                      ? 'bg-white text-black shadow-lg'
                      : 'bg-black text-white shadow-lg'
                    : d
                      ? 'text-gray-400 hover:bg-white/5 hover:text-gray-100'
                      : 'text-gray-600 hover:bg-black/5 hover:text-gray-900'
                }`}
              >
                {Icon && (
                  <Icon
                    size={18}
                    className={isActive ? (d ? 'text-black' : 'text-white') : d ? 'text-gray-400' : 'text-gray-500'}
                  />
                )}
                <span className="flex-1 text-right">{item.label}</span>
                {item.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    isActive
                      ? d ? 'bg-black/20 text-black' : 'bg-white/20 text-white'
                      : d ? 'bg-white/10 text-gray-300' : 'bg-black/10 text-gray-600'
                  }`}>
                    {item.count}
                  </span>
                )}
                {item.badge > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full animate-pulse font-bold ${d ? 'bg-white text-black' : 'bg-black text-white'}`}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom slot (extra buttons like center code) */}
        {bottomSlot && (
          <div className={`p-4 space-y-2 ${d ? 'border-t border-white/10' : 'border-t border-black/10'}`}>
            {bottomSlot}
          </div>
        )}
      </aside>

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <main className="lg:mr-64 mt-16 min-h-[calc(100vh-4rem)] relative z-10 transition-colors duration-300">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>

        {/* ══════════════ FOOTER ══════════════ */}
        <footer
          className="mt-12"
          style={{
            background: d ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderTop: d ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 pt-6 pb-4">
            {/* Top Section: Logo/Name */}
            <div className="text-center mb-6">
              <h2 className={`text-3xl font-black ${d ? 'text-white' : 'text-black'}`}>الأستاذ محمد حامد</h2>
            </div>

            {/* Middle Section: Copyright & Developers */}
            <div className="text-center mb-8">
              <p className={`text-sm mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>جميع الحقوق محفوظة © 2026</p>
              <div className="text-center" dir="ltr">
                <span className="text-red-500">&lt;</span>
                <span className="text-blue-500">Developed by</span>
                <span className="text-green-500">=</span>
                <span className="text-red-500">"</span>
                
                {/* Marwan Sami */}
                <span className="relative group inline-block">
                  <span className="text-yellow-500 cursor-pointer">Marwan Sami (ابو ريان)</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg z-50 w-32">
                    <a href="https://wa.me/201064756266" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                    <a href="https://www.instagram.com/maro.og7/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                      <Instagram size={12} /> Instagram
                    </a>
                  </div>
                </span>
                
                <span className="text-red-500"> , </span>
                
                {/* Anas Hany */}
                <span className="relative group inline-block">
                  <span className="text-yellow-500 cursor-pointer">Anas Hany (ابو ادام)</span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col bg-slate-800 text-white text-xs rounded-lg p-2 shadow-lg z-50 w-32">
                    <a href="https://wa.me/201028608446" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                    <a href="https://www.instagram.com/anas_aboadam555/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:bg-slate-700 px-2 py-1 rounded transition-colors">
                      <Instagram size={12} /> Instagram
                    </a>
                  </div>
                </span>
                
                <span className="text-red-500">"</span>
                <span className="text-blue-500"> andPowered</span>
                <span className="text-green-500">=</span>
                <span className="text-orange-500">true</span>
                <span className="text-red-500"> /&gt;</span>
              </div>
            </div>

            {/* Bottom Section: Two Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-right">
              {/* Right Column: الصفحات */}
              <div>
                <h4 className={`text-lg font-bold mb-4 ${d ? 'text-white' : 'text-black'} text-red-500`}>الصفحات</h4>
                <div className="flex flex-col gap-3">
                  <a
                    href="/"
                    className={`font-bold text-sm flex items-center justify-end gap-2 transition-colors ${d ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                  >
                    الرئيسية
                  </a>
                  <a
                    href="https://wa.me/201028608446?text=السلام عليكم، لدي مشكلة في منصة سبيل التفوق وهي: "
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-bold text-sm flex items-center justify-end gap-2 transition-colors ${d ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                  >
                    المساعدة / الدعم الفني
                  </a>
                </div>
              </div>

              {/* Left Column: السوشيال ميديا */}
              <div>
                <h4 className={`text-lg font-bold mb-4 ${d ? 'text-white' : 'text-black'} text-red-500`}>السوشيال ميديا</h4>
                <div className="flex items-center justify-end gap-4">
                  <a
                    href="https://www.facebook.com/share/1LQkyhgFRt/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${d ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-black'}`}
                    title="فيسبوك"
                  >
                    <Facebook size={22} />
                  </a>
                  <a
                    href="https://youtube.com/channel/UCHJwRJSN1A1bOIVWholqQMg?si=RQHu2xXSgF2ReDRl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${d ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-black'}`}
                    title="يوتيوب"
                  >
                    <Youtube size={22} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </main>

      <div className="fixed bottom-8 left-8 z-50 flex flex-col items-start" dir="rtl">
        {showHelpMenu && (
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md shadow-xl rounded-2xl p-3 mb-4 flex flex-col gap-2 border border-green-500/20">
            <a
              href="https://wa.me/201028608446"
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-slate-900 text-white hover:bg-emerald-500 transition-colors"
            >
              <MessageCircle size={18} />
              الدعم الفني (ابو ادام)
            </a>
            <a
              href="https://wa.me/201064756266"
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-slate-900 text-white hover:bg-emerald-500 transition-colors"
            >
              <MessageCircle size={18} />
              مروان سامي
            </a>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowHelpMenu(!showHelpMenu)}
          className="w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 transition-all cursor-pointer transform hover:scale-105"
        >
          <MessageCircle size={28} />
        </button>
      </div>
    </div>
  );
};